/**
 * Renders the actual HTML + CSS for a single toast notification
 * (mostly by delegating to toast-package).
 */
import type {CSSProperties} from "aphrodite";
import * as React from "react";

import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import {ErrorToast, Toast} from "@khan/component-toast";

import {
    getToastLiveRegion,
    updateToastLiveRegion,
} from "./toast-live-region.ts";

type Props = {
    // The content of the toast notification
    children?: React.ReactNode;
    // You can include a standardized link at the bottom of the toast
    linkText?: string;
    // The HREF for the link
    href?: string;
    // Time in ms when to automatically close the toast notification
    autoTimeout?: number;
    // Is this an error toast?
    isError: boolean;
    // An optional test id for use in tests
    testId?: string;
    // Styles for toast container.
    style?: CSSProperties;
    // Color for close icon.
    closeIconColor?: string;
} & WithActionSchedulerProps;

type State = {
    show: boolean;
};

class BaseToastNotification extends React.Component<Props, State> {
    static defaultProps: {
        autoTimeout: number;
        isError: boolean;
    } = {
        autoTimeout: 6000, // 6s recommended for better a11y by https://sheribyrnehaber.medium.com/designing-toast-messages-for-accessibility-fb610ac364be
        isError: false,
    };

    state: State = {
        show: true,
    };

    updateLiveRegion(node?: HTMLElement | null) {
        if (!node) {
            return;
        }

        /* JH - this live region works on Talkback/Android/Chrome and
        JAWS/Windows/Chrome. When a dialog is open is does not work at all on
        VoiceOver/Safari/[either iOS or OSX]. When a dialog is not open it
        works on VoiceOver/Safari/OSX as well.

        Do not test on OSX outside of Safari - there is some weirdness but this
        is not a setup we support

        How to test with the assignment complete notification

        * As a student, open an assigned article
        * Wait for the notification to appear (sometimes this takes a moment)
        * Listen for the message to be read to you. Because it's polite the
        screen reader should finish reading what it's currently reading and
        then read the message when there is a pause
         */

        const region = getToastLiveRegion();
        // update the live region on a delay. 250ms is what I tested first and
        // it worked (test cycles are slow, see above). Tried using an
        // animation frame, tested only on TalkBack/Android and the message was
        // not read
        this.props.schedule.timeout(() => {
            // @ts-expect-error - TS2345 - Argument of type 'Node' is not assignable to parameter of type 'HTMLElement'.
            updateToastLiveRegion(region, node.cloneNode(true));
        }, 250);
    }

    render(): React.ReactNode {
        if (!this.state.show) {
            return null;
        }

        const {
            children,
            linkText,
            href,
            autoTimeout,
            isError,
            testId,
            style,
            closeIconColor,
        } = this.props;

        return isError ? (
            <ErrorToast
                linkText={linkText}
                href={href}
                onClose={() => this.setState({show: false})}
                testId={testId}
                style={style}
                closeIconColor={closeIconColor}
            >
                {children}
            </ErrorToast>
        ) : (
            <Toast
                linkText={linkText}
                href={href}
                onClose={() => this.setState({show: false})}
                autoTimeout={autoTimeout}
                testId={testId}
                style={style}
                closeIconColor={closeIconColor}
            >
                <div
                    ref={(node) => {
                        this.updateLiveRegion(node);
                    }}
                >
                    {children}
                </div>
            </Toast>
        );
    }
}

type ExportProps = WithoutActionScheduler<
    JSX.LibraryManagedAttributes<
        typeof BaseToastNotification,
        React.ComponentProps<typeof BaseToastNotification>
    >
>;

export default withActionScheduler(
    BaseToastNotification,
) as React.ComponentType<ExportProps>;
